<template>
  <main-container
    :class="[
      {'from-all' : BreadcrumbAssistant.fromAll($route)},
      {'from-entity-landing' : BreadcrumbAssistant.fromEntityLanding($route)}
    ]"
    class="task-detail-topbar"
    size="full"
    no-bottom-padding
  >
    <div class="task-detail-topbar__navigation">
      <breadcrumb-for-item-detail
        :entity-type="EEntities.PROJECTS"
        :title="projectName"
        :selected-entity-id="projectId"
        :item-title="taskTitle"
        :item-ref-number="refNumber"
        :show-item-title="revealExpandedInfo"
        @change-filter="(val) => $emit('change-filter', val)"
        @add-custom-filter-clicked="$emit('add-custom-filter-clicked')"
        @navigate="(item) => $emit('navigate', item)"
        @back-button-clicked="$emit('back-button-clicked')"
      />
    </div>
    <!-- DESKTOP ONLY: Show when OPTIONS is scrolled out of viewport -->
    <template v-if="$permissions.isNotGuest && $screen.isDesktop($mq)">
      <div v-if="revealExpandedInfo" class="task-detail-topbar__picker-options">
        <template v-if="!disablePickerOptions">
          <item-detail-picker-options
            v-model:assignee-id="internalAssigneeID"
            v-model:priority-id="internalPriorityID"
            v-model:status-id="internalStatusID"
            v-model:due-date="internalDueDate"
            :assignee-options="assigneesOptions"
            :priority-options="priorityOptions"
            :status-options="statusOptions"
            :job-info="jobInfo"
          />
          <span class="vertical-divider" />
        </template>
        <task-detail-meta-options
          :global-attachments="globalAttachments"
          :is-pinned="isPinned"
          :show-harvest-option="showHarvestOption"
          @show-harvest-modal="$emit('show-harvest-modal')"
          @delete-clicked="$emit('delete-clicked')"
          @pin-toggled="$emit('pin-toggled')"
        />
      </div>
      <div v-else class="task-detail-topbar__new-task-wrapper">
        <new-task-button :project-id="projectId" :project-name="projectName" size="medium" />
      </div>
    </template>
  </main-container>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import MainContainer from '@/layouts/main-container.vue'

/* Mixins */

/* Components */
import TaskDetailMetaOptions from './task-detail-meta-options.vue'

/* Helpers */
import BreadcrumbAssistant from '@/helpers/breadcrumb-assistant'
import RouteAssistant from '@/helpers/route-assistant'

/* Data types */
import { EEntities } from '@/types'
import {
  IPersonWithEmail,
  IPriorityLevelForSelection,
  IStatusForSelection,
} from '@/api/types/response.types'
import { IAttachment } from '@/types/data-objects/attachments'
import { IJobInfo } from '@/types/data-objects/items'

/* API */

export default defineComponent({
  name: 'TaskDetailTopbar',

  components: {
    MainContainer,
    TaskDetailMetaOptions,
  },

  props: {
    refNumber: {
      type: Number,
      required: true,
    },

    taskTitle: {
      type: String,
      required: true,
    },

    projectId: {
      type: Number,
      required: true,
    },

    projectName: {
      type: String,
      required: true,
    },

    assigneeId: {
      type: Number,
      default: null,
    },

    priorityId: {
      type: Number,
      required: true,
    },

    statusId: {
      type: Number,
      required: true,
    },

    dueDate: {
      type: [Date, String],
      default: null,
    },

    statusOptions: {
      type: Array as () => IStatusForSelection[],
      required: true,
    },

    priorityOptions: {
      type: Array as () => IPriorityLevelForSelection[],
      required: true,
    },

    assigneesOptions: {
      type: Array as () => IPersonWithEmail[],
      required: true,
    },

    globalAttachments: {
      type: Array as () => IAttachment[],
      required: true,
    },

    jobInfo: {
      type: Object as () => IJobInfo,
      default: null,
    },

    showHarvestOption: {
      type: Boolean,
      required: true,
    },

    disablePickerOptions: {
      type: Boolean,
      required: true,
    },

    isPinned: {
      type: Boolean,
      required: true,
    },
  },

  emits: [
    'add-custom-filter-clicked',
    'back-button-clicked',
    'change-filter',
    'delete-clicked',
    'navigate',
    'pin-toggled',
    'show-harvest-modal',
    'update-assignee',
    'update-dueDate',
    'update-priority',
    'update-status',
  ],

  data() {
    return {
      EEntities,
      RouteAssistant,
      BreadcrumbAssistant,
      revealExpandedInfo: false,
    }
  },

  computed: {
    hasGlobalAttachments(): boolean {
      return this.globalAttachments.length > 0
    },
    internalStatusID: {
      get(): any {
        return this.statusId
      },
      set(newValue: any) {
        if (this.statusId !== newValue) {
          this.$emit('update-status', newValue)
        }
      },
    },

    internalPriorityID: {
      get(): any {
        return this.priorityId
      },
      set(newValue: any) {
        if (this.priorityId !== newValue) {
          this.$emit('update-priority', newValue)
        }
      },
    },

    internalDueDate: {
      get(): any {
        return this.dueDate
      },
      set(newValue: any) {
        if (this.dueDate !== newValue) {
          this.$emit('update-dueDate', newValue)
        }
      },
    },

    internalAssigneeID: {
      get(): any {
        return this.assigneeId
      },
      set(newValue: any) {
        if (this.assigneeId !== newValue) {
          this.$emit('update-assignee', newValue)
        }
      },
    },
  },

  mounted() {
    document.addEventListener('scroll', this.checkScrollPosition)
  },

  beforeUnmount() {
    document.removeEventListener('scroll', this.checkScrollPosition)
  },

  methods: {
    checkScrollPosition() {
      // Low-fi estimated height where the title/meta options is scrolled up.
      // Didn't want to get too sophisticated here.
      this.revealExpandedInfo = window.scrollY > 250
    },
  },
})
</script>

<style lang="scss">
.task-detail-topbar {
  position: sticky;
  top: -2px;
  z-index: 1031;
  background-color: var(--color-background-primary);

  &__navigation {
    display: flex;
    align-items: center;
    padding-bottom: 19px;
    margin-bottom: 12px;
    border-bottom: $border--dotted-light;
    width: 100%;

    @include media-breakpoint-down(sm) {
      padding-right: $detail-gutter-pad-sm;
      padding-left: $detail-gutter-pad-sm;
      border-bottom: none;
      margin-bottom: 0;
    }
  }

  &__new-task-wrapper {
    position: absolute;
    right: 8px;
    top: 20px;
  }

  :root {
    /* Light mode */
    --gradient-transparent: rgba(0, 0, 0, 0);
    --gradient-background: rgba(250, 249, 246, 1); /* Your light beige color */
  }

  @media (prefers-color-scheme: dark) {
    :root {
      --gradient-transparent: rgba(0, 0, 0, 0);
      --gradient-background: rgba(
        35,
        35,
        40,
        1
      ); /* Dark mode color - adjust as needed */
    }
  }
  &__picker-options {
    animation-name: dd-fade-in;
    animation-duration: 0.4s;
    position: absolute;
    right: 9px;
    top: 10px;
    display: flex;
    align-items: center;
    background-color: var(--color-background-primary);
    padding-left: 60px;
    background: linear-gradient(
      90deg,
      var(--gradient-transparent) 0%,
      var(--gradient-background) 8%,
      var(--gradient-background) 100%
    );
    .vertical-divider {
      width: 1px;
      height: 24px;
      margin: 0 12px;
      background-color: $color--gray-30;
    }
  }
}
</style>
